/*
  <lp-sticky-atc> — sticky add-to-cart bar for the landing pages.

  Ported from lightning-theme's sticky-bar: same visibility model (a trigger
  element observed with IntersectionObserver, plus hiding once the footer is
  reached) and the same /cart/add.js call. Adapted here for a scroll-percentage
  trigger, which is how the frames describe it ("persistent from ~20%"), and for
  selects that drive the price.
*/
if (!customElements.get('lp-sticky-atc')) {
  customElements.define(
    'lp-sticky-atc',
    class LpStickyAtc extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('[data-atc]');
        this.formulaSelect = this.querySelector('[data-formula-select]');
        this.packSelect = this.querySelector('[data-pack-select]');
        this.variantSelect = this.querySelector('[data-variant-select]');
        this.variantLink = this.querySelector('[data-variant-link]');
        this.priceOutput = this.querySelector('[data-price-output]');
        this.compareOutput = this.querySelector('[data-compare-output]');
        this.thumbImg = this.querySelector('.lp-sticky-atc__thumb-img');

        // Formula -> pack sizes -> one variant and its own selling plan. The
        // map is rendered by the section so no request is needed to switch.
        const raw = this.querySelector('[data-sticky-formulas]');
        try {
          this.formulas = raw ? JSON.parse(raw.textContent) : [];
        } catch (e) {
          console.error('[lp-sticky-atc] bad formula data', e);
          this.formulas = [];
        }

        this.initVisibility();
        this.initFooterObserver();

        // The bar is 88px on desktop and 103px on phones, so the offset the
        // widgets ride on has to be re-measured when the breakpoint changes.
        this.onResize = () => this.syncWidgets();
        window.addEventListener('resize', this.onResize, { passive: true });
        if (this.formulaSelect) {
          this.renderFormulas();
          this.formulaSelect.addEventListener('change', () => this.renderPacks());
        }
        if (this.packSelect) {
          this.packSelect.addEventListener('change', () => this.onPackChange());
        }
        if (this.variantSelect) {
          this.variantSelect.addEventListener('change', () => this.onVariantChange());
        }
        if (this.formulas.length) {
          this.restoreSelection();
          this.renderPacks();
        }
        // Belt and braces for any browser that still restores the selects
        // after a history navigation: recompute price and link from whatever
        // the selects show once the page is fully back.
        this.onPageShow = () => this.renderPacks();
        window.addEventListener('pageshow', this.onPageShow);
        if (this.button) {
          this.button.addEventListener('click', () => this.addToCart());
        }
      }

      disconnectedCallback() {
        this.triggerObserver?.disconnect();
        this.footerObserver?.disconnect();
        if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
        if (this.onResize) window.removeEventListener('resize', this.onResize);
        if (this.onPageShow) window.removeEventListener('pageshow', this.onPageShow);
        // The widgets outlive the bar; drop them back where they were.
        document.documentElement.classList.remove('lp-sticky-atc-open');
      }

      /* ---------- visibility ---------- */

      show(on) {
        // `hidden` keeps the bar out of the layout and the a11y tree until it is
        // due; the class drives the transition.
        if (on) this.hidden = false;
        this.classList.toggle('is-visible', on);
        this.syncWidgets();
      }

      /* ---------- floating widgets ----------

         The Kodif chat iframe and CookieYes' revisit button are app-injected and
         fixed to the bottom of the viewport well above this bar's z-index, so
         they sit on top of it — the chat over the CTA, the cookie button over
         the thumbnail. Neither element belongs to this theme, so the bar
         publishes its own height and open state and lets the stylesheet lift
         them. Both only exist while the bar is on the page. */

      syncWidgets() {
        const root = document.documentElement;
        const height = Math.round(this.getBoundingClientRect().height);
        // `hidden` collapses the bar to nothing; keep the last real height so the
        // widgets do not snap to the floor mid-transition.
        if (height > 0) {
          root.style.setProperty('--lp-sticky-atc-height', height + 'px');
        }
        root.classList.toggle('lp-sticky-atc-open', this.classList.contains('is-visible'));
      }

      initVisibility() {
        const selector = (this.dataset.triggerSelector || '').trim();
        const target = selector ? document.querySelector(selector) : null;

        if (target) {
          // Once the trigger has been scrolled past, the bar stays put.
          this.triggerObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting || entry.boundingClientRect.top < 0) {
                  this.show(true);
                  this.triggerObserver.disconnect();
                }
              });
            },
            { root: null, threshold: 0 }
          );
          this.triggerObserver.observe(target);
          return;
        }

        const percent = parseFloat(this.dataset.scrollPercent);
        if (!percent) {
          this.show(true);
          return;
        }

        this.onScroll = () => {
          const scrollable = document.documentElement.scrollHeight - window.innerHeight;
          if (scrollable <= 0) return;
          const progress = (window.scrollY / scrollable) * 100;
          if (progress >= percent) {
            this.show(true);
            window.removeEventListener('scroll', this.onScroll);
            this.onScroll = null;
          }
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();
      }

      initFooterObserver() {
        if (this.dataset.hideAtFooter !== 'true') return;
        // Queried in priority order, not as one selector list: a selector list
        // returns the first match in DOCUMENT order, and the review cards each
        // render a <footer>, so the bar was hiding halfway up the page when a
        // review scrolled into view. The page footer is the last resort.
        const footer = ['.lp-footer', '.custom-footer', 'body > footer'].reduce(
          (found, selector) => found || document.querySelector(selector),
          null
        );
        if (!footer) return;

        this.footerObserver = new IntersectionObserver(
          (entries) => {
            // Only ever hides — the trigger above owns showing it.
            if (entries[0].isIntersecting) this.classList.remove('is-visible');
            else if (this.hidden === false) this.classList.add('is-visible');
            this.syncWidgets();
          },
          { root: null, threshold: 0 }
        );
        this.footerObserver.observe(footer);
      }

      /* ---------- price ---------- */

      /* Both paths — the formula/pack pair and the legacy variant select —
         write the price through here, so the struck compare price behaves the
         same either way. */
      updatePrice(price, compare) {
        if (this.priceOutput) this.priceOutput.textContent = this.money(price);
        if (this.compareOutput) {
          const show = Number(compare) > Number(price);
          this.compareOutput.textContent = show ? this.money(compare) : '';
          this.compareOutput.classList.toggle('is-hidden', !show);
        }
      }

      onVariantChange() {
        const option = this.variantSelect.selectedOptions[0];
        if (!option) return;
        if (this.button) this.button.dataset.variantId = option.value;
        this.updatePrice(Number(option.dataset.price || 0), Number(option.dataset.compareAt || 0));
      }

      money(cents) {
        const rate = window.Shopify?.currency?.rate ?? 1;
        const amount = (rate * cents) / 100;
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'en', {
            style: 'currency',
            currency: window.Shopify?.currency?.active || 'USD',
            minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
          }).format(amount);
        } catch (e) {
          return '$' + amount.toFixed(2);
        }
      }

      /* ---------- formula + pack ---------- */

      /* The selection survives a trip to the PDP and back. Browsers restore
         <select> values on back navigation without firing `change`, so the bar
         used to show "Sleep · 30 bottles" while price and link still pointed at
         the default formula's first pack. The selects opt out of that
         restoration and the bar keeps its own copy of the choice instead, keyed
         by handle and pack count so a reordered formula list cannot swap it. */
      get storageKey() {
        return 'lp-sticky-atc:' + (this.dataset.sectionId || '');
      }

      saveSelection() {
        const formula = this.activeFormula;
        const pack = this.activePack;
        if (!formula || !pack) return;
        try {
          sessionStorage.setItem(
            this.storageKey,
            JSON.stringify({
              label: formula.label,
              handle: formula.handle,
              count: pack.count,
            })
          );
        } catch (e) {
          // Storage can be blocked (private mode, disabled cookies); the bar
          // still works, it just forgets the selection on the way back.
        }
      }

      restoreSelection() {
        let saved = null;
        try {
          saved = JSON.parse(sessionStorage.getItem(this.storageKey));
        } catch (e) {
          return;
        }
        if (!saved) return;
        const index = this.formulas.findIndex(
          (f) => f.handle === saved.handle && f.label === saved.label
        );
        if (index < 0) return;
        if (this.formulaSelect) this.formulaSelect.value = String(index);
        // renderPacks() keeps the current pack value where the formula offers
        // it, so the count only has to be on the select before it runs.
        if (this.packSelect) {
          const option = document.createElement('option');
          option.value = saved.count;
          this.packSelect.appendChild(option);
          this.packSelect.value = saved.count;
        }
      }

      /* The formula select is filled from the same map the packs come from, so
         a product configured in the section shows up in both without the
         labels being written out twice. */
      renderFormulas() {
        this.formulaSelect.innerHTML = '';
        this.formulas.forEach((formula, i) => {
          const option = document.createElement('option');
          option.value = String(i);
          option.textContent = formula.label;
          this.formulaSelect.appendChild(option);
        });
      }

      get activeFormula() {
        if (!this.formulas.length) return null;
        const i = this.formulaSelect ? Number(this.formulaSelect.value) : 0;
        return this.formulas[i] || this.formulas[0];
      }

      /* Rebuilds the pack select for the chosen formula, keeping the same pack
         count selected where that formula offers it — switching Original to
         Sleep should not silently drop a 60 back to a 15. */
      renderPacks() {
        const formula = this.activeFormula;
        if (!formula || !this.packSelect) return;

        const wanted = this.packSelect.value;
        this.packSelect.innerHTML = '';
        formula.packs.forEach((pack) => {
          const option = document.createElement('option');
          option.value = pack.count;
          option.textContent = pack.label;
          option.disabled = !pack.available;
          this.packSelect.appendChild(option);
        });

        const keep = formula.packs.some((p) => String(p.count) === String(wanted));
        this.packSelect.value = keep ? wanted : (formula.packs[0] && formula.packs[0].count) || '';

        if (this.thumbImg && formula.image) this.thumbImg.src = formula.image;
        this.onPackChange();
      }

      get activePack() {
        const formula = this.activeFormula;
        if (!formula) return null;
        const value = this.packSelect ? this.packSelect.value : null;
        return (
          formula.packs.find((p) => String(p.count) === String(value)) || formula.packs[0] || null
        );
      }

      onPackChange() {
        const pack = this.activePack;
        if (!pack) return;
        this.updatePrice(pack.price, pack.compare);
        if (this.button) {
          this.button.dataset.variantId = pack.variant;
          // Blank means the variant sells one-time only; addToCart omits it.
          this.button.dataset.sellingPlan = pack.plan || '';
          this.button.disabled = !pack.available;
        }

        // In link mode the CTA carries the selection to the product page, so
        // the PDP opens on the same variant and plan the bar was showing.
        if (this.variantLink) {
          const formula = this.activeFormula;
          const params = new URLSearchParams({ variant: pack.variant });
          if (pack.plan) params.set('selling_plan', pack.plan);
          this.variantLink.href = (formula.url || '') + '?' + params.toString();
        }

        this.saveSelection();
      }

      /* ---------- add to cart ---------- */

      async addToCart() {
        if (this.button.disabled) return;
        this.button.classList.add('is-loading');
        try {
          const item = {
            id: Number(this.button.dataset.variantId),
            quantity: 1,
          };
          // The pack size carries its own plan, exactly as the PDP posts it.
          const plan = this.button.dataset.sellingPlan;
          if (plan) item.selling_plan = Number(plan);
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({ items: [item] }),
          });
          if (!response.ok) throw new Error('cart/add failed: ' + response.status);
          const data = await response.json();
          // The hybrid cart listens for this; the theme also refreshes on it.
          document.dispatchEvent(
            new CustomEvent('cart:added', {
              detail: { item: data.items ? data.items[0] : data },
            })
          );
        } catch (error) {
          console.error('[lp-sticky-atc]', error);
        } finally {
          this.button.classList.remove('is-loading');
        }
      }
    }
  );
}
